// The workout ENERGY READOUT bundle (HC-8). One derivation of a session's
// energy-view inputs (Metria's calorie estimate, the cardio heart rate, and
// %HRmax) so every surface that shows <CaloriesEffortBlock> (mobile logger
// footer + post-save card, web-rnw session detail) computes it ONCE here
// instead of hand-assembling the same props. Pure, IO-free.
//
// The bundle's field names line up 1:1 with CaloriesEffortBlockProps (minus
// theme), so a caller spreads it straight in: `{...readout}`.
//
// Age is passed in (via `ageFromBucket` at the callsite) — Profile stores only
// an AgeBucket, and keeping the source out of here mirrors hrEffort.ts's
// age-source-agnostic shape.

import { percentHrMax } from "./hrEffort.js";
import type { MovementPattern } from "./schemas/muscle.js";
import { hasCardioTrait } from "./sessionCalorieEstimate.js";
import type { CaloriesEstimate } from "./schemas/session.js";
import type { WorkoutExerciseEntry } from "./schemas/workout.js";

export type WorkoutEnergyReadout = {
  /** Metria's calorie estimate (null when the session carries none). #290:
   *  the only calorie figure; a Health Connect value is never shown. */
  estimate: CaloriesEstimate | null;
  /** Heart rate, only on a session with a cardio bout (#290). */
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** #156: total distance of the cardio sets, in metres; absent when none. */
  distanceM?: number;
  /** avg-HR as %HRmax, or null when HR/age missing. */
  hrMaxPct: number | null;
  /** LG-46 — the live count includes a set the athlete hasn't done yet, so the
   *  reading is a projection of the whole session, not a measurement of work
   *  already logged. Only the live path sets it; a saved session leaves it off.
   *  The (i) disclosure uses it to declare the projection. */
  projected?: boolean;
  /** LEGAL-5 ripiego (2026-09-09): MET-only twin of `estimate`, HR stripped
   *  (calorieEstimate.ts's OWN MET route, not a second engine), the share
   *  card's fallback when the estimate is Keytel (fed by a Health Connect HR).
   *  Set only by callers that hold the raw per-set ingredients to recompute it
   *  (the live draft; a saved session's assembled Workout). Absent on a thin
   *  Session-envelope-only caller (web's session-detail view), where the
   *  ripiego cell then stays omitted. */
  metFallbackKcal?: number | null;
};

type PatternsBySlug = Readonly<Record<string, MovementPattern | null | undefined>>;

/** The energy-bearing fields a session (or an assembled Workout carrying them
 *  read-only) exposes. */
export type EnergyBearingSession = {
  caloriesEstimate?: CaloriesEstimate;
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** The sets, when the caller holds them (an assembled Workout does, the thin
   *  Session envelope does not). #156: an imported cardio session keeps its
   *  heart rate and distance on the cardio set, so the readout reads them
   *  from here when the session itself carries none. */
  entries?: readonly (Pick<WorkoutExerciseEntry, "sets"> &
    Partial<Pick<WorkoutExerciseEntry, "exerciseId">>)[];
};

/** Heart rate and distance of a session's cardio sets (#156). Read-only: it
 *  derives from the sets as stored, nothing is written back. The average is
 *  weighted by set duration; a set with no heart rate does not dilute it. */
export function cardioSetTotals(
  entries: EnergyBearingSession["entries"],
): { avgHrBpm?: number; distanceM?: number } {
  let hrWeighted = 0;
  let hrSeconds = 0;
  let distanceM = 0;
  for (const entry of entries ?? []) {
    for (const set of entry.sets) {
      if (set.type !== "cardio") continue;
      if (set.avgHrBpm !== undefined) {
        hrWeighted += set.avgHrBpm * set.duration;
        hrSeconds += set.duration;
      }
      distanceM += set.distanceM ?? 0;
    }
  }
  return {
    ...(hrSeconds > 0 ? { avgHrBpm: Math.round(hrWeighted / hrSeconds) } : {}),
    ...(distanceM > 0 ? { distanceM } : {}),
  };
}

/** True when the session has a cardio bout, the only place heart rate counts
 *  (#290: Keytel is validated on aerobic effort, and wrist HR during lifting is
 *  unreliable). With the sets at hand it reads them; on a thin envelope it
 *  falls back to the estimate, which is Keytel only when a cardio bout used HR.
 *  Who lends heart rate: only a cardio bout, the same rule the sync applies to
 *  Health Connect activities (`HcActivityAction` in
 *  apps/mobile/src/healthconnect/exerciseTypeMap.ts). Since #290 (2026-10-26)
 *  an HIIT counts too: a `timed` / `bodyweight` set of a cardio-pattern
 *  exercise, which needs `patternsBySlug` (the sets do not carry the pattern). */
function hasCardioBout(
  session: EnergyBearingSession,
  patternsBySlug: PatternsBySlug | undefined,
): boolean {
  if (session.entries === undefined) {
    return session.caloriesEstimate?.method === "keytel";
  }
  return session.entries.some((e) => {
    const movementPattern =
      e.exerciseId !== undefined ? patternsBySlug?.[e.exerciseId] : undefined;
    return e.sets.some((s) => hasCardioTrait({ kind: s.type, movementPattern }));
  });
}

/** Derive the full energy readout from a session's stored fields. `ageYears`
 *  comes from `ageFromBucket(profile.ageBucket)`. A calorie value stored from
 *  Health Connect (`calories`) is ignored, and heart rate shows only on a
 *  session with a cardio bout: the stored rows stay, they are not shown. */
export function sessionEnergyReadout(
  session: EnergyBearingSession,
  opts: { ageYears: number | undefined; patternsBySlug?: PatternsBySlug },
): WorkoutEnergyReadout {
  const fromSets = cardioSetTotals(session.entries);
  const cardio = hasCardioBout(session, opts.patternsBySlug);
  const avgHrBpm = cardio ? (session.avgHrBpm ?? fromSets.avgHrBpm) : undefined;
  const peakHrBpm = cardio ? session.peakHrBpm : undefined;
  return {
    estimate: session.caloriesEstimate ?? null,
    ...(avgHrBpm !== undefined ? { avgHrBpm } : {}),
    ...(peakHrBpm !== undefined ? { peakHrBpm } : {}),
    ...(fromSets.distanceM !== undefined ? { distanceM: fromSets.distanceM } : {}),
    hrMaxPct: percentHrMax(avgHrBpm, opts.ageYears),
  };
}
